import { EvaluationTimeSlot, SlotAvailability } from 'domains/calendars/v3/useEvaluationCalendarSummary';
import { timeRangeValues } from 'domains/signupDirect/misc/constants';
import { z } from 'zod';

/** Simplified since we don't really have to validate EVERY field, we basically just want to know if its undefined or not and cast
 * the type to our existing type. This is because fields aren't manually entered by the user individually but programmatically as a whole object
 */
const TimeSlotSchema = z
  .object({ time: z.date() }, { message: 'Please choose a time slot' })
  .passthrough()
  .transform(d => d as EvaluationTimeSlot);

/** Used by experimental schedule-by-timeslot experiment
 * @todo(juan): cleanup once experiment is done
 */
const TimeSlotSchemaBySlot = z
  .object({ time: z.date() }, { message: 'Please choose a time slot' })
  .passthrough()
  .transform(d => d as SlotAvailability);

export const baseSchema = z.object({
  timeZone: z.string(),
});

const timeOptionsSchemaShape = Object.fromEntries(timeRangeValues.map(value => [value, z.boolean()]));

const TimeOptionsSchema = z
  .object(timeOptionsSchemaShape)
  .optional()
  .refine(obj => {
    const values = Object.values(obj || {});
    const timeRangeSelected = values.filter(value => value).length > 1;
    return !timeRangeSelected;
  });

export const SingleEvaluationSchema = baseSchema.extend({
  type: z.literal('single'),
  timeSlot: TimeSlotSchema,
  timeOptions: TimeOptionsSchema,
});

export const SplitEvaluationSchemaV1 = baseSchema.extend({
  type: z.literal('split-by-therapist'),
  firstTimeSlot: TimeSlotSchema,
  secondTimeSlot: TimeSlotSchema.optional(),
});

export const SplitEvaluationSchemaV2 = SplitEvaluationSchemaV1.extend({
  type: z.literal('split-by-slot'),

  firstTimeSlotRange: TimeSlotSchemaBySlot,
  secondTimeSlotRange: TimeSlotSchemaBySlot.optional(),
});

export const EvaluationScheduleSchema = z.discriminatedUnion('type', [
  SingleEvaluationSchema,
  SplitEvaluationSchemaV2,
  SplitEvaluationSchemaV1,
]);

export type SingleEvaluationSchemaType = z.infer<typeof SingleEvaluationSchema>;
export type SplitEvaluationSchemaTypeV1 = z.infer<typeof SplitEvaluationSchemaV1>;
export type SplitEvaluationSchemaTypeV2 = z.infer<typeof SplitEvaluationSchemaV2>;
export type SplitEvaluationSchemaType = SplitEvaluationSchemaTypeV1 | SplitEvaluationSchemaTypeV2;
export type EvaluationScheduleFormType = z.infer<typeof EvaluationScheduleSchema>;

export const isSplitEvaluationSchema = (
  evaluationSchedule: EvaluationScheduleFormType,
): evaluationSchedule is SplitEvaluationSchemaType => {
  return evaluationSchedule.type === 'split-by-slot' || evaluationSchedule.type === 'split-by-therapist';
};
